import {buttonStyles} from './styles';

type ButtonElement = HTMLAnchorElement | HTMLButtonElement;

const BUTTON_SELECTOR = '[data-ui-button]:not([data-ui-loading-mode="false"])';
const REQUEST_ATTR_PREFIXES = [
    'data-on',
    'data-bind',
    'data-ref',
    'data-show',
    'data-action',
    'data-request',
    'data-ui-request',
    'wire:',
];
const REQUEST_FINISH_EVENT = 'ui:request:finish';

let cleanupSubmitGuard: (() => void) | null = null;

const isButtonElement = (value: EventTarget | null): value is ButtonElement => (
    value instanceof HTMLButtonElement || value instanceof HTMLAnchorElement
);

const closestButton = (target: EventTarget | null): ButtonElement | null => {
    if (!(target instanceof Element)) return null;
    const button = target.closest<ButtonElement>(BUTTON_SELECTOR);
    return isButtonElement(button) ? button : null;
};

const hasRequestAttribute = (button: ButtonElement): boolean => (
    button.getAttributeNames().some((name) => REQUEST_ATTR_PREFIXES.some((prefix) => name.startsWith(prefix)))
);

const isSubmitButton = (button: ButtonElement): boolean => (
    button instanceof HTMLButtonElement && button.type === 'submit'
);

const hasSubmitConfirmation = (button: ButtonElement): boolean => (
    button instanceof HTMLButtonElement
    && (
        button.dataset.uiConfirm !== undefined
        || button.form?.dataset.uiConfirm !== undefined
    )
);

const submitEventHasConfirmation = (form: HTMLFormElement | null, submitter: HTMLElement | null): boolean => (
    submitter?.dataset.uiConfirm !== undefined
    || form?.dataset.uiConfirm !== undefined
);

const shouldGuardClick = (button: ButtonElement): boolean => (
    button.dataset.uiLoadingMode === 'true'
    || isSubmitButton(button)
    || hasRequestAttribute(button)
);

const isLocked = (button: ButtonElement): boolean => (
    button.dataset.uiSubmitting === 'true'
    || button.getAttribute('aria-disabled') === 'true'
    || button.hasAttribute('disabled')
);

const shouldSkipScheduledLock = (button: ButtonElement): boolean => (
    button.dataset.uiSubmitting === 'true'
    || button.hasAttribute('disabled')
);

const lockButton = (button: ButtonElement): void => {
    button.dataset.uiSubmitting = 'true';
    button.setAttribute('data-ui-loading', '');
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
    button.dispatchEvent(new CustomEvent('ui:loading-start', {bubbles: true}));

    if (button instanceof HTMLButtonElement) {
        button.disabled = true;
    } else {
        button.tabIndex = -1;
    }
};

const unlockButton = (button: ButtonElement): void => {
    if (button.dataset.initialLoading === 'true') return;

    delete button.dataset.uiSubmitting;
    button.removeAttribute('data-ui-loading');
    button.removeAttribute('aria-busy');

    if (button.dataset.initialDisabled === 'true') return;

    button.removeAttribute('aria-disabled');
    if (button instanceof HTMLButtonElement) {
        button.disabled = false;
    } else {
        button.removeAttribute('tabindex');
    }

    button.dispatchEvent(new CustomEvent('ui:loading-end', {bubbles: true}));
};

const scheduleLock = (button: ButtonElement, event: Event): void => {
    window.setTimeout(() => {
        if (!button.isConnected || shouldSkipScheduledLock(button)) return;
        lockButton(button);
    }, 0);
};

const guardClick = (event: Event): void => {
    const button = closestButton(event.target);
    if (!button || !shouldGuardClick(button)) return;

    if (isLocked(button)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
    }

    if (button instanceof HTMLButtonElement && button.form && !button.form.checkValidity()) return;
    if (hasSubmitConfirmation(button)) return;

    scheduleLock(button, event);
};

const guardSubmit = (event: Event): void => {
    const submitEvent = event as SubmitEvent;
    const nativeSubmitter = submitEvent.submitter instanceof HTMLElement ? submitEvent.submitter : null;
    const submitter = isButtonElement(nativeSubmitter) ? nativeSubmitter : null;
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    const button = submitter
        ? submitter.matches(BUTTON_SELECTOR) ? submitter : null
        : form?.querySelector<ButtonElement>(BUTTON_SELECTOR) ?? null;

    if (event.defaultPrevented && submitEventHasConfirmation(form, nativeSubmitter)) return;

    if (!button || !isSubmitButton(button)) return;

    scheduleLock(button, event);
};

const unlockSubmittedButtons = (): void => {
    document.querySelectorAll<ButtonElement>('[data-ui-button][data-ui-submitting="true"]')
        .forEach((button) => unlockButton(button));
};

const registerButton = (): void => {
    if (cleanupSubmitGuard) return;

    document.addEventListener('click', guardClick, true);
    document.addEventListener('submit', guardSubmit, true);
    document.addEventListener(REQUEST_FINISH_EVENT, unlockSubmittedButtons);

    cleanupSubmitGuard = () => {
        document.removeEventListener('click', guardClick, true);
        document.removeEventListener('submit', guardSubmit, true);
        document.removeEventListener(REQUEST_FINISH_EVENT, unlockSubmittedButtons);
        cleanupSubmitGuard = null;
    };
};

const unregisterButton = (): void => {
    cleanupSubmitGuard?.();
};

export {registerButton, unregisterButton};
